import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, CheckCircle, Heart } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const WellnessGenerationTrigger: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [forceRegeneration, setForceRegeneration] = useState(false);

  const runWellnessGeneration = async () => {
    setIsRunning(true);
    setError(null);
    setSuccess(null);

    try {
      const { data, error } = await supabase.functions.invoke('trigger-wellness-generation-cron', {
        body: { 
          force_regeneration: forceRegeneration 
        }
      });

      if (error) {
        console.error('Error:', error);
        setError(error.message || 'Failed to trigger cron job');
        toast.error('Failed to trigger cron job: ' + (error.message || 'Unknown error'));
      } else {
        setSuccess('Weekly wellness generation cron job triggered successfully. Check the Cron Job Execution History below for results.');
        toast.success('Weekly wellness generation cron job triggered successfully!');
      }
    } catch (err: any) {
      console.error('Failed to trigger cron job:', err);
      setError(err.message || 'Failed to trigger cron job');
      toast.error('Failed to trigger cron job: ' + (err.message || 'Unknown error'));
    } finally {
      setIsRunning(false);
    }
  };


  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Trigger Wellness Generation
          </CardTitle>
          <CardDescription>
            Manually trigger the wellness generation cron job to create weekly wellness assessments for all families.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Checkbox
              id="forceRegeneration"
              checked={forceRegeneration}
              onCheckedChange={(checked) => setForceRegeneration(checked === true)}
            />
            <label htmlFor="forceRegeneration" className="text-sm">
              Force regeneration (overwrite existing assessments)
            </label>
          </div>

          <Button 
            onClick={runWellnessGeneration} 
            disabled={isRunning}
            className="w-full"
          >
            {isRunning ? 'Triggering Generation...' : 'Trigger Wellness Generation'}
          </Button>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-800">{success}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};